import { useEffect } from 'react';
import { usePermStore } from '@/admin/auth/usePermStore';
import type { RequiredPerms } from '@/admin/auth/permissions';

interface CanProps {
  perm: string | string[];
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

/** Stale-while-revalidate perm check hook (never suspends). */
export function useCan() {
  const has = usePermStore((s) => s.has);
  const isStale = usePermStore((s) => s.isStale);
  const isFetching = usePermStore((s) => s.isFetching);

  useEffect(() => {
    if (isStale() && !isFetching) {
      void usePermStore.getState().fetchPermissions();
    }
  }, [isStale, isFetching]);

  return (perm: string | RequiredPerms): boolean => has(perm);
}

/**
 * Conditional render on permission(s) (M3 S3.1.3).
 * Dev-only missing-perm console.debug; UI never enumerates perms.
 */
export function Can({ perm, fallback = null, children }: CanProps) {
  const can = useCan();
  const allowed = can(perm);
  if (!allowed && import.meta.env.DEV) {
    console.debug('[Can] hidden (no enumeration in UI)');
  }
  return allowed ? <>{children}</> : <>{fallback}</>;
}

export default Can;
